import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
// material
import {
  Container,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import { red, green } from '@mui/material/colors';
import { styled } from '@mui/material/styles';

// context
import { EventContext } from '../../contexts/Event';
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';

// services
import { validateTicketQRCode } from '../../services/Order';
import { getEntryList } from '../../services/Report';
import { getTimeZoneGTM, getTimeZoneDate, getTimeZoneDateStart } from '../../helpers/Util';

// components
import Page from '../../components/Page';

const QRCodeContainer = styled(Box)(({ theme }) => ({
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: '400px',
  height: '400px',
  marginBottom: theme.spacing(2)
}));

export default function PageQRCodeScanner() {
  const [searchParams] = useSearchParams();
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { event } = useContext(EventContext);
  const [pause, setPause] = useState(false);
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  let ranking = -1;

  useEffect(() => {
    const fetchData = async () => {
      try {
        let eventDate = getTimeZoneDateStart({ date: new Date() }).toISOString();

        const queryEventDate = searchParams.get('eventDate');

        if (queryEventDate) {
          eventDate = getTimeZoneDate({ date: queryEventDate }).toISOString();
        }

        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const entries = await getEntryList({ headers, eventId: event._id, eventDate });
        const totalEntries = entries
          .filter((entry) => entry.reasons.length === 0)
          .reduce((total) => total + 1, 0);

        setTotalEntries(totalEntries);
        setEntries(entries);
        setIsLoading(false);
      } catch (error) {
        setHasError(true);
        console.error(error);
      }
    };

    if (event?._id) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [searchParams, authUser, authSettings, event, setEntries]);

  const validateTicket = async (result) => {
    setPause(true);

    const [scanResult] = result;

    // TODO: REMOVER, é para teste
    // result = {
    //   boundingBox: {
    //     x: 223,
    //     y: 397,
    //     width: 157,
    //     height: 157,
    //     top: 397,
    //     right: 380,
    //     bottom: 554,
    //     left: 223
    //   },
    //   rawValue: 'c35f06d4-c7eb-4433-a2ab-e811b120e012',
    //   format: 'qr_code',
    //   cornerPoints: [
    //     {
    //       x: 223,
    //       y: 423
    //     },
    //     {
    //       x: 356,
    //       y: 397
    //     },
    //     {
    //       x: 380,
    //       y: 526
    //     },
    //     {
    //       x: 249,
    //       y: 554
    //     }
    //   ]
    // };

    const idToken = await authUser.getIdToken();
    const headers = {
      Authorization: `Bearer ${idToken}`,
      'Account-Key': authSettings.server.path
    };

    const payload = {
      eventId: event._id,
      qrcode: scanResult.rawValue
    };

    try {
      const entry = await validateTicketQRCode({ headers, payload });
      setEntries((currentEntries) => [entry, ...currentEntries]);
      setTotalEntries((currentTotalEntries) =>
        entry.reasons.length === 0 ? currentTotalEntries + 1 : currentTotalEntries
      );
    } catch (err) {
      setHasError(true);
      console.error(err);
    }

    if (navigator.vibrate) {
      navigator.vibrate(200);
    }

    setPause(false);
  };

  if (hasError) {
    return (
      <Container>
        <Alert severity="error">An error occurred! Please, refresh the page and try again.</Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return (
    <Page title="QRCode Scanner" path="/qrcode/scanner">
      <Container>
        <QRCodeContainer>
          <Scanner
            onScan={(result) => validateTicket(result)}
            onError={(error) => console.error(error)}
            paused={pause}
          />
        </QRCodeContainer>
      </Container>
      <List>
        {entries.map((entry, i) => (
          <ListItem key={i}>
            {entry.reasons.length === 0 ? (
              <Box sx={{ background: green[50], borderRadius: '20px', p: 2, flexGrow: 1 }}>
                <Box display="flex" alignItems="start" justifyContent="between">
                  <Box mb={1}>
                    <Typography variant="span" color="text.secondary">
                      Entry time at {`(GTM${getTimeZoneGTM({ date: entry.booking.entry.time })})`}
                    </Typography>
                    <Typography component="div" variant="p">
                      <strong>
                        {`${getTimeZoneDate({ date: entry.booking.entry.time })
                          .tz('Europe/Lisbon')
                          .format('ddd, DD MMM, YYYY - HH:mm:ss')}`}
                      </strong>
                    </Typography>
                  </Box>
                  <ListItemText
                    sx={{ textAlign: 'right' }}
                    primary={
                      <Typography component="div" variant="p">
                        <strong>
                          #{totalEntries - (ranking += 1)} <br />
                        </strong>
                      </Typography>
                    }
                  />
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Order
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>#{entry.order.number}</strong>
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Section
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>
                      {entry.booking.section.single ? ' - ' : entry.booking.section.name}
                    </strong>
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Ticket
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>{entry.booking.ticket.name}</strong>
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  background: red[50],
                  border: `8px solid ${red[500]}`,
                  borderRadius: '20px',
                  p: 2,
                  flexGrow: 1
                }}
              >
                <Box display="flex" alignItems="start" justifyContent="between">
                  <Box mb={1}>
                    <Typography variant="span" color="text.secondary">
                      Order
                    </Typography>
                    <Typography component="div" variant="p">
                      <strong>#{entry?.order?.number || ' - '}</strong>
                    </Typography>
                  </Box>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Code
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>{entry?.booking?.qrcode || ' - '}</strong>
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Event
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>{entry?.order?.number ? entry.event.name : ' - '}</strong>
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Event Date {`(GTM${getTimeZoneGTM({ date: entry.event.date })})`}
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>
                      {entry?.order?.number
                        ? `${getTimeZoneDate({ date: entry.event.date })
                            .tz('Europe/Lisbon')
                            .format('ddd, DD MMM, YYYY - HH:mm:ss')}`
                        : ' - '}
                    </strong>
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Section
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>
                      {entry?.booking?.section?.single
                        ? ' - '
                        : entry?.booking?.section?.name || ' - '}
                    </strong>
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Ticket
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>{entry?.booking?.ticket?.name || ' - '}</strong>
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Status
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>{entry?.booking?.status || ' - '}</strong>
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Entry Between {`(GTM${getTimeZoneGTM({ date: entry.event.date })})`}
                  </Typography>
                  <Typography component="div" variant="p">
                    {entry?.booking?.entry ? (
                      <strong>
                        {`${getTimeZoneDate({ date: entry.booking.entry.starts })
                          .tz('Europe/Lisbon')
                          .format('ddd, DD MMM, YYYY - HH:mm:ss')}`}
                        {` (GTM${getTimeZoneGTM({ date: entry.booking.entry.starts })})`}
                        {' - '}
                        {`${getTimeZoneDate({ date: entry.booking.entry.ends })
                          .tz('Europe/Lisbon')
                          .format('ddd, DD MMM, YYYY - HH:mm:ss')}`}
                        {` (GTM${getTimeZoneGTM({ date: entry.booking.entry.ends })})`}
                      </strong>
                    ) : (
                      ' - '
                    )}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Reasons" size="small" color="error" />
                </Divider>
                <Box mb={1}>
                  {entry.reasons.map((reason, i) => (
                    <Typography key={i} component="div" variant="p" mb={1} color="error">
                      - {reason}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </ListItem>
        ))}
      </List>
    </Page>
  );
}
